import React, { useState, useEffect, useCallback, KeyboardEvent, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Database as DatabaseIcon, 
  Loader2, 
  Search, 
  Upload, 
  File, 
  FileText, 
  Image as ImageIcon, 
  FileArchive, 
  Video, 
  AudioLines,
  Trash2, 
  Download, 
  ExternalLink,
  HelpCircle,
  Cloud,
  Link,
  ChevronDown,
  ArrowUpDown,
  Expand,
  Check,
  FileIcon,
  Calendar,
  Folder,
  Sparkles,
  Clock,
  Crop,
  RotateCcw,
  Sliders,
  User,
  Play
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from '@/components/ui/dropdown-menu';
import { Database as DatabaseTypes } from '../../../database.types';

type ResourceType = DatabaseTypes['public']['Enums']['resource_type'];
type ResourceCache = DatabaseTypes['public']['Tables']['resource_cache']['Row'];
type Json = DatabaseTypes['public']['Tables']['resource_cache']['Insert']['metadata'];

// New interface for picker mode props
interface BrandResourcesProps {
  mode?: 'manager' | 'picker';
  onResourceSelect?: (resource: ResourceCache) => void;
  initialSelectedResource?: string;
  onCancel?: () => void;
  allowedTypes?: ResourceType[];
  brandId?: string; // Add optional direct brandId prop
}

function isResourceMetadata(metadata: Json | null): metadata is { [key: string]: Json | undefined } {
  return metadata !== null && typeof metadata === 'object' && !Array.isArray(metadata);
}

function getFileSize(metadata: Json | null): number | null {
  if (!isResourceMetadata(metadata)) return null;
  const fileSize = metadata.file_size;
  return typeof fileSize === 'number' ? fileSize : null;
}

// DragAndDropUploadZone component with updated props
const DragAndDropUploadZone: React.FC<{
  onFilesUploaded: (files: File[]) => void;
  onAddResource: (
    type: ResourceType, 
    description: string,
    placeholder: string,
    httpContentType?: string | null,
    urlToInject?: string | null
  ) => Promise<void>;
  isCompact?: boolean;
}> = ({ onFilesUploaded, onAddResource, isCompact = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);
      onFilesUploaded(filesArray);
    }
  }, [onFilesUploaded]);
  
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      onFilesUploaded(filesArray);
    }
  }, [onFilesUploaded]);
  
  if (isCompact) {
    return (
      <div
        className={`relative overflow-hidden border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center
          cursor-pointer transition-all duration-300 shadow-sm hover:shadow-md ${
          isDragging
            ? 'border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50'
            : 'border-emerald-200 bg-gradient-to-br from-emerald-25 to-white hover:border-emerald-300'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('dropzone-file-compact')?.click()}
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative text-center">
          <div className={`p-2 rounded-full mx-auto mb-2 transition-all duration-300 ${
            isDragging
              ? 'bg-emerald-100 text-emerald-600 scale-110'
              : 'bg-emerald-50 text-emerald-500'
          }`}>
            <Cloud className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-emerald-700">Drop files here or click to upload</p>
        </div>
        <input
          id="dropzone-file-compact"
          type="file"
          className="hidden"
          multiple
          onChange={handleFileInputChange}
        />
      </div>
    );
  }
  
  return (
    <div
      className={`relative overflow-hidden border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center
        cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl ${
        isDragging
          ? 'border-emerald-400 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 shadow-emerald-200/50 scale-[1.02]'
          : 'border-emerald-200 bg-gradient-to-br from-white via-emerald-25 to-white hover:border-emerald-300 hover:shadow-emerald-100/50'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => document.getElementById('dropzone-file')?.click()}
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />

      <div className="relative text-center">
        <div className={`mb-6 p-4 rounded-2xl mx-auto transition-all duration-300 bg-white/80 backdrop-blur-sm shadow-lg ${
          isDragging
            ? 'text-emerald-600 scale-110 shadow-emerald-200/50'
            : 'text-emerald-500 hover:scale-105'
        }`}>
          <Cloud className="h-10 w-10 mx-auto" />
        </div>

        <h3 className="text-xl font-bold text-emerald-800 mb-2">
          Drag & drop files here or click to upload
        </h3>
        <p className="text-emerald-600 mb-6 max-w-md">
          Supports images, videos, audio files, documents and data files. Upload multiple files at once.
        </p>

        <input
          id="dropzone-file"
          type="file"
          className="hidden"
          multiple
          onChange={handleFileInputChange}
        />

        <div className="flex gap-3 justify-center">
          <Button type="button" className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg hover:shadow-xl transition-all duration-300">
            <Upload className="h-4 w-4 mr-2" />
            Upload Files
          </Button>
          <AddViaUrlButton onAddResource={onAddResource} />
        </div>
      </div>
    </div>
  );
};

// Pexels Photo Interface
interface PexelsPhoto {
  id: number;
  width: number;
  height: number;
  url: string;
  photographer: string;
  photographer_url: string;
  photographer_id: number;
  avg_color: string;
  src: {
    original: string;
    large2x: string;
    large: string;
    medium: string;
    small: string;
    portrait: string;
    landscape: string;
    tiny: string;
  };
  liked: boolean;
  alt: string;
}

// Pexels Video Interface
interface PexelsVideo {
  id: number;
  width: number;
  height: number;
  url: string;
  image: string;
  full_res: string | null;
  tags: string[];
  duration: number;
  user: {
    id: number;
    name: string;
    url: string;
  };
  video_files: {
    id: number;
    quality: string;
    file_type: string;
    width: number;
    height: number;
    fps: number;
    link: string;
  }[];
  video_pictures: {
    id: number;
    picture: string;
    nr: number;
  }[];
}

// Pexels Search Modal Component
const PexelsSearchModal: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMediaSelected: (url: string, type: 'image' | 'video', metadata: any) => void;
  brandId: string;
}> = ({ open, onOpenChange, onMediaSelected, brandId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'photos' | 'videos'>('photos');
  const [results, setResults] = useState<(PexelsPhoto | PexelsVideo)[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<PexelsPhoto | PexelsVideo | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  
  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setResults([]);
      setSelectedMedia(null);
      setPage(1);
      setHasMore(true);
    }
  }, [open]);
  
  const searchPexels = async (query: string, pageNum: number = 1, append: boolean = false) => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      // Since we don't have a frontend API key, we'll call through our backend
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No active session');
      }
      
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      if (!workerUrl) {
        throw new Error('Worker URL not configured');
      }
      
      const response = await fetch(`${workerUrl}/rainfactory/content-functions/pexels-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({
          query,
          type: searchType,
          page: pageNum,
          per_page: 20
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to search Pexels');
      }
      
      const data = await response.json();
      const newResults = searchType === 'photos' ? data.photos : data.videos;
      
      if (append) {
        setResults(prev => [...prev, ...newResults]);
      } else {
        setResults(newResults);
      }
      
      setHasMore(newResults.length === 20); // Assuming 20 per page
      
    } catch (error) {
      console.error('Error searching Pexels:', error);
      toast({
        title: 'Search Failed',
        description: error instanceof Error ? error.message : 'Failed to search Pexels',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    searchPexels(searchQuery, 1, false);
  };
  
  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    searchPexels(searchQuery, nextPage, true);
  };
  
  const downloadAndUpload = async (media: PexelsPhoto | PexelsVideo) => {
    setDownloadingId(media.id);
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No active session');
      }
      
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      if (!workerUrl) {
        throw new Error('Worker URL not configured');
      }
      
      // Determine the URL to download based on media type
      let downloadUrl: string;
      let filename: string;
      let description: string;
      
      if (searchType === 'photos') {
        const photo = media as PexelsPhoto;
        downloadUrl = photo.src.large;
        filename = `pexels-${photo.photographer.replace(/\s+/g, '-').toLowerCase()}-${photo.id}.jpg`;
        description = photo.alt || `Photo by ${photo.photographer}`;
      } else {
        const video = media as PexelsVideo;
        // Use the highest quality video file available
        const bestQuality = video.video_files.find(f => f.quality === 'hd') || video.video_files[0];
        downloadUrl = bestQuality.link;
        filename = `pexels-${video.user.name.replace(/\s+/g, '-').toLowerCase()}-${video.id}.mp4`;
        description = `Video by ${video.user.name}`;
      }
      
      // Call backend to download and upload the media
      const response = await fetch(`${workerUrl}/rainfactory/content-functions/pexels-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({
          brandId,
          url: downloadUrl,
          filename,
          description,
          type: searchType === 'photos' ? 'image' : 'video',
          metadata: {
            pexels_id: media.id,
            pexels_url: media.url,
            photographer: searchType === 'photos' ? (media as PexelsPhoto).photographer : (media as PexelsVideo).user.name,
            photographer_url: searchType === 'photos' ? (media as PexelsPhoto).photographer_url : (media as PexelsVideo).user.url,
            width: media.width,
            height: media.height,
            source: 'stock_media'
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to download media');
      }
      
      const result = await response.json();
      
      toast({
        title: 'Media Added',
        description: 'The media has been downloaded and added to your resources',
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });
      
      // Close modal and trigger refresh
      onOpenChange(false);
      
      // Trigger parent component refresh by calling onMediaSelected
      onMediaSelected(downloadUrl, searchType === 'photos' ? 'image' : 'video', {
        resourceId: result.resourceId,
        pexels_id: media.id
      });
      
    } catch (error) {
      console.error('Error downloading media:', error);
      toast({
        title: 'Download Failed',
        description: error instanceof Error ? error.message : 'Failed to download media',
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Search Stock</DialogTitle>
          <DialogDescription>
            Search for high-quality free photos and videos
          </DialogDescription>
        </DialogHeader>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search for ${searchType}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Tabs value={searchType} onValueChange={(value) => setSearchType(value as 'photos' | 'videos')} className="">
            <TabsList>
              <TabsTrigger value="photos">
                <ImageIcon className="h-4 w-4 mr-1" /> Photos
              </TabsTrigger>
              <TabsTrigger value="videos">
                <Video className="h-4 w-4 mr-1" /> Videos
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button type="submit" disabled={loading || !searchQuery.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </form>
        
        {/* Results Grid */}
        <div className="flex-1 overflow-y-auto">
          {results.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-1">
                {results.map((media) => (
                  <div key={media.id} className="group relative">
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer"
                         onClick={() => setSelectedMedia(media)}>
                      {searchType === 'photos' ? (
                        <img
                          src={(media as PexelsPhoto).src.medium}
                          alt={(media as PexelsPhoto).alt}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                      ) : (
                        <div className="relative w-full h-full">
                          <img
                            src={(media as PexelsVideo).image}
                            alt={`Video by ${(media as PexelsVideo).user.name}`}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-black/60 rounded-full p-2">
                              <Video className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Quick download button */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="bg-black/60 hover:bg-black/80 text-white border-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadAndUpload(media);
                        }}
                        disabled={downloadingId === media.id}
                      >
                        {downloadingId === media.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    
                    {/* Attribution */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-xs truncate">
                        {searchType === 'photos' 
                          ? `Photo by ${(media as PexelsPhoto).photographer}`
                          : `Video by ${(media as PexelsVideo).user.name}`
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center mt-4">
                  <Button 
                    variant="outline" 
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              {loading ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                  <p>Searching Pexels...</p>
                </>
              ) : searchQuery ? (
                <>
                  <Search className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No results found for "{searchQuery}"</p>
                </>
              ) : (
                <>
                  <ImageIcon className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">Search for high-quality photos and videos</p>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Media Preview Modal */}
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent className="sm:max-w-[800px]">
            {selectedMedia && (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {searchType === 'photos' 
                      ? `Photo by ${(selectedMedia as PexelsPhoto).photographer}`
                      : `Video by ${(selectedMedia as PexelsVideo).user.name}`
                    }
                  </DialogTitle>
                  <DialogDescription>
                    {searchType === 'photos' 
                      ? (selectedMedia as PexelsPhoto).alt
                      : `${selectedMedia.width}×${selectedMedia.height} • ${Math.floor((selectedMedia as PexelsVideo).duration)}s`
                    }
                  </DialogDescription>
                </DialogHeader>
                
                <div className="flex justify-center">
                  {searchType === 'photos' ? (
                    <img
                      src={(selectedMedia as PexelsPhoto).src.large}
                      alt={(selectedMedia as PexelsPhoto).alt}
                      className="max-w-full max-h-[50vh] object-contain rounded-md"
                    />
                  ) : (
                    <video
                      src={(selectedMedia as PexelsVideo).video_files[0]?.link}
                      poster={(selectedMedia as PexelsVideo).image}
                      controls
                      className="max-w-full max-h-[50vh] rounded-md"
                    />
                  )}
                </div>
                
                <DialogFooter>
                  <div className="flex items-center gap-2 mr-auto">
                    <Badge variant="outline">
                      {selectedMedia.width}×{selectedMedia.height}
                    </Badge>
                    <a
                      href={searchType === 'photos' 
                        ? (selectedMedia as PexelsPhoto).photographer_url
                        : (selectedMedia as PexelsVideo).user.url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                    >
                      <User className="h-3 w-3" />
                      {searchType === 'photos' 
                        ? (selectedMedia as PexelsPhoto).photographer
                        : (selectedMedia as PexelsVideo).user.name
                      }
                    </a>
                  </div>
                  <Button variant="outline" onClick={() => setSelectedMedia(null)}>
                    Close
                  </Button>
                  <Button 
                    onClick={() => downloadAndUpload(selectedMedia)}
                    disabled={downloadingId === selectedMedia.id}
                  >
                    {downloadingId === selectedMedia.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Add to Resources
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        
        <DialogFooter>
          <div className="text-xs text-muted-foreground mr-auto">
            Photos and videos provided by <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Pexels</a>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// AddViaUrlButton component
const AddViaUrlButton: React.FC<{
  onAddResource: (
    type: ResourceType,
    description: string,
    placeholder: string,
    httpContentType?: string | null,
    urlToInject?: string | null
  ) => Promise<void>;
}> = ({ onAddResource }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [resourceType, setResourceType] = useState<ResourceType>('api');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const resetForm = () => {
    setResourceType('api');
    setUrl('');
    setDescription('');
    setIsSubmitting(false);
  };
  
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      if (!url) {
        toast({
          title: 'Missing URL',
          description: 'Please enter a valid URL',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }
      
      // Validate URL format
      try {
        new URL(url);
      } catch (e) {
        toast({
          title: 'Invalid URL',
          description: 'Please enter a valid URL including http:// or https://',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }
      
      const placeholder = `${resourceType}_${Date.now()}`;
      await onAddResource(
        resourceType,
        description || 'External resource',
        placeholder,
        resourceType === 'api' ? 'application/json' : null,
        url
      );
      
      setIsOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error adding resource:', error);
      toast({
        title: 'Failed to Add Resource',
        description: error instanceof Error ? error.message : 'An error occurred adding the resource',
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="bg-white/80 backdrop-blur-sm border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 text-emerald-700"
      >
        <Link className="h-4 w-4 mr-2" />
        Add via URL
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px] bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/40 border border-emerald-200/50">
        <DialogHeader className="text-center pb-4">
          <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
            <Link className="h-6 w-6 text-emerald-600" />
          </div>
          <DialogTitle className="text-xl font-bold text-emerald-800">Add External Resource</DialogTitle>
          <DialogDescription className="text-emerald-600">
            Add a resource via URL or API endpoint to your brand library
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="resource-type" className="text-right">
              Type
            </Label>
            <Select
              value={resourceType}
              onValueChange={(value) => setResourceType(value as ResourceType)}
            >
              <SelectTrigger className="col-span-3" id="resource-type">
                <SelectValue placeholder="Select resource type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Image URL</SelectItem>
                <SelectItem value="video">Video URL</SelectItem>
                <SelectItem value="audio">Audio URL</SelectItem>
                <SelectItem value="document">Document URL</SelectItem>
                <SelectItem value="api">API Endpoint</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="url" className="text-right">
              URL
            </Label>
            <Input
              id="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/resource"
              className="col-span-3"
            />
          </div>
          
          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="description" className="text-right pt-2">
              Description
            </Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this resource"
              className="col-span-3"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setIsOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !url}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding Resource...
              </>
            ) : (
              'Add Resource'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

// Utility function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Utility function to get icon by resource type
const getResourceIcon = (type: ResourceType, httpContentType?: string | null) => {
  switch (type) {
    case 'image':
      return <ImageIcon className="h-8 w-8 text-blue-500" />;
    case 'video':
      return <Video className="h-8 w-8 text-red-500" />;
    case 'audio':
      return <AudioLines className="h-8 w-8 text-green-500" />;
    case 'document':
      return <FileText className="h-8 w-8 text-amber-500" />;
    case 'data':
      return <DatabaseIcon className="h-8 w-8 text-purple-500" />;
    case 'api':
      return <ExternalLink className="h-8 w-8 text-cyan-500" />;
    default:
      // Fallback to http_content_type if available
      if (httpContentType) {
        if (httpContentType.startsWith('image/')) {
          return <ImageIcon className="h-8 w-8 text-blue-500" />;
        } else if (httpContentType.startsWith('video/')) {
          return <Video className="h-8 w-8 text-red-500" />;
        } else if (httpContentType.startsWith('audio/')) {
          return <AudioLines className="h-8 w-8 text-green-500" />;
        } else if (
          httpContentType.startsWith('application/pdf') ||
          httpContentType.startsWith('text/')
        ) {
          return <FileText className="h-8 w-8 text-amber-500" />;
        } else if (
          httpContentType.startsWith('application/zip') || 
          httpContentType.startsWith('application/x-rar') ||
          httpContentType.startsWith('application/x-7z')
        ) {
          return <FileArchive className="h-8 w-8 text-orange-500" />;
        }
      }
      return <File className="h-8 w-8 text-gray-500" />;
  }
};

// Update ResourceCard component to support picker mode
const ResourceCard: React.FC<{ 
  resource: ResourceCache; 
  onDelete: (id: string) => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  compactMode?: boolean;
  pickerMode?: boolean;
  focusable?: boolean;
  focused?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>, id: string) => void;
}> = ({ 
  resource, 
  onDelete, 
  selected = false, 
  onToggleSelect, 
  compactMode = false, 
  pickerMode = false,
  focusable = false,
  focused = false,
  onKeyDown
}) => {
  const resourceIcon = getResourceIcon(resource.type, resource.http_content_type);
  const date = new Date(resource.created_at).toLocaleDateString();
  const [showFullImage, setShowFullImage] = useState(false);
  const [showHoverActions, setShowHoverActions] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
      const [isVideoHovered, setIsVideoHovered] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [editedDescription, setEditedDescription] = useState('');
    
    // Initialize edited description when dialog opens
    useEffect(() => {
      if (showFullImage) {
        console.log('Initializing description:', resource.description);
        setEditedDescription(resource.description || '');
      }
    }, [showFullImage, resource.description]);
      
  // Check if resource is pending upload
  const isPendingUpload = isResourceMetadata(resource.metadata) && 
    resource.metadata.pending_upload === true;
  
  // Check if resource upload had an error
  const hasUploadError = isResourceMetadata(resource.metadata) && 
    resource.metadata.upload_error === true;
  
  // Check if resource was AI-generated
  const isAIGenerated = isResourceMetadata(resource.metadata) && 
    resource.metadata.ai_generated === true;
  
  // Function to extract filename from url_to_inject if available
  const getDisplayName = () => {
    if (resource.url_to_inject) {
      try {
        const url = new URL(resource.url_to_inject);
        const pathSegments = url.pathname.split('/');
        const fileName = pathSegments[pathSegments.length - 1];
        if (fileName) return decodeURIComponent(fileName);
      } catch (e) {
        // If URL parsing fails, just return the placeholder
      }
    }
    return resource.placeholder || resource.id;
  };
  
  const displayName = getDisplayName();
  
  const handleDownload = async () => {
    if (resource.type === 'api' && resource.url_to_inject) {
      window.open(resource.url_to_inject, '_blank');
      return;
    }
    
    if (resource.url_to_inject) {
      // Create a download link and trigger it
      const a = document.createElement('a');
      a.href = resource.url_to_inject;
      a.download = displayName;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      toast({
        title: 'Resource Not Available',
        description: 'This resource does not have a downloadable URL yet.',
        variant: 'destructive',
      });
    }
  };

  // Extract image dimensions for image resources
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  
  useEffect(() => {
    if (resource.type === 'image' && resource.url_to_inject && resource.processed) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = resource.url_to_inject;
    }
  }, [resource.type, resource.url_to_inject, resource.processed]);
  
  // Video hover effects
  useEffect(() => {
    if (resource.type === 'video' && videoRef.current) {
      const video = videoRef.current;
      
      if (isVideoHovered) {
        video.currentTime = 0;
        video.muted = true;
        video.play().catch(() => {
          // Silently handle play errors (e.g., autoplay restrictions)
        });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  }, [isVideoHovered, resource.type]);
  
  // Determine if we should show an image preview
  const shouldShowImagePreview = resource.type === 'image' && resource.url_to_inject && resource.processed;
  
  // Determine if we should show a video preview
  const shouldShowVideoPreview = resource.type === 'video' && resource.url_to_inject && resource.processed;
  
  // Get file size from metadata if available
  const fileSize = getFileSize(resource.metadata);
  const formattedFileSize = fileSize ? formatFileSize(fileSize) : null;
  
  // Extract source label from metadata if available
  const sourceLabel = isResourceMetadata(resource.metadata) && typeof resource.metadata.source === 'string'
    ? (resource.metadata.source as string)
    : null;
  
  // Handle keyboard navigation
  const handleKeyboardNavigation = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (onKeyDown) {
      onKeyDown(e, resource.id);
    }
  };

  // Get video dimensions from metadata if available
  const getVideoDimensions = () => {
    if (isResourceMetadata(resource.metadata)) {
      const width = resource.metadata.width as number;
      const height = resource.metadata.height as number;
      if (width && height) {
        return { width, height };
      }
    }
    return null;
  };

  const videoDimensions = getVideoDimensions();

  // Render compact mode
  if (compactMode) {
    return (
      <Card
        className={`overflow-hidden transition-all duration-300 relative border-emerald-200/50 bg-white/90 backdrop-blur-sm ${
          pickerMode ? 'cursor-pointer hover:shadow-lg hover:shadow-emerald-100/50' : ''
        } ${
          selected ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-200/50 bg-emerald-50/40' : pickerMode ? 'hover:ring-2 hover:ring-emerald-300/50 hover:bg-emerald-50/20' : ''
        } ${focused ? 'ring-2 ring-emerald-400/70' : ''} ${
          isPendingUpload ? 'animate-pulse bg-emerald-50/30' : ''
        } ${hasUploadError ? 'border-red-300 bg-red-50/30' : ''}`}
        onMouseEnter={() => setShowHoverActions(true)}
        onMouseLeave={() => setShowHoverActions(false)}
        onClick={() => onToggleSelect && onToggleSelect(resource.id)}
        tabIndex={focusable ? 0 : undefined}
        onKeyDown={handleKeyboardNavigation}
        aria-selected={selected}
      >
        <div className="flex items-center p-3 gap-3">
          {/* Selection checkmark for picker mode */}
          {pickerMode && (
            <div className={`absolute top-2 right-2 z-10 rounded-full w-6 h-6 flex items-center justify-center transition-all shadow-sm ${
              selected
                ? 'bg-emerald-500 text-white scale-110'
                : 'bg-white/90 text-muted-foreground border-2 border-emerald-200'
            } ${showHoverActions && !selected ? 'scale-105 border-emerald-300' : ''}`}>
              {selected ? <Check className="h-4 w-4 stroke-[3]" /> : <div className="h-4 w-4" />}
            </div>
          )}
          
          {!pickerMode && onToggleSelect && (
            <div className="flex-shrink-0">
              <Checkbox 
                checked={selected}
                onCheckedChange={() => onToggleSelect(resource.id)}
                aria-label="Select resource"
              />
            </div>
          )}
          
          <div className="flex-shrink-0">
            {shouldShowImagePreview ? (
              <div
                className="w-12 h-12 rounded-md overflow-hidden border border-muted"
                onClick={(e) => {
                  if (!pickerMode) {
                    e.stopPropagation();
                    setShowFullImage(true);
                  }
                }}
              >
                <img
                  src={resource.url_to_inject!}
                  alt={resource.description}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : shouldShowVideoPreview ? (
              <div
                className="w-12 h-12 rounded-md overflow-hidden border border-muted relative"
                onMouseEnter={() => setIsVideoHovered(true)}
                onMouseLeave={() => setIsVideoHovered(false)}
                onClick={(e) => {
                  if (!pickerMode) {
                    e.stopPropagation();
                    setShowFullImage(true);
                  }
                }}
              >
                <video 
                  ref={videoRef}
                  src={resource.url_to_inject!}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                />
                {!isVideoHovered && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>
            ) : (
              <div className="w-12 h-12 flex items-center justify-center rounded-md bg-muted/40">
                {resourceIcon}
              </div>
            )}
          </div>
          
          <div className="flex-grow min-w-0">
            <div className="flex flex-col gap-1">
              <div className="font-medium text-sm line-clamp-3">{resource.description}</div>
              <div className="flex items-center flex-wrap gap-x-2 text-xs text-muted-foreground">
                {(!resource.url_to_inject || isPendingUpload) && (
                  <Badge variant="outline" className={`px-1.5 py-0 h-5 font-normal ${
                    isPendingUpload ? 'bg-blue-50 text-blue-700 border-blue-200' : ''
                  } ${hasUploadError ? 'bg-red-50 text-red-700 border-red-200' : ''}`}>
                    {isPendingUpload ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Uploading
                      </>
                    ) : hasUploadError ? (
                      <>
                        <Trash2 className="h-3 w-3 mr-1" />
                        Failed
                      </>
                    ) : (
                      'Processing'
                    )}
                  </Badge>
                )}
                
                {isAIGenerated && (
                  <Badge variant="secondary" className="px-1.5 py-0 h-5 font-normal">
                    <Sparkles className="h-3 w-3 mr-1" /> AI
                  </Badge>
                )}
                
                {formattedFileSize && (
                  <span className="text-xs text-muted-foreground">{formattedFileSize}</span>
                )}
                
                {imageDimensions && (
                  <span className="text-xs text-muted-foreground">{imageDimensions.width}×{imageDimensions.height}</span>
                )}
                
                {videoDimensions && (
                  <span className="text-xs text-muted-foreground">{videoDimensions.width}×{videoDimensions.height}</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 flex items-center gap-1">
            {/* View button - ONLY shows on hover in manager mode */}
            {!pickerMode && (shouldShowImagePreview || shouldShowVideoPreview) && showHoverActions && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullImage(true);
                }}
              >
                <Expand className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        

        
        {/* Image Editing Modal - placeholder for now */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Edit Image</DialogTitle>
              <DialogDescription>Crop, rotate, or adjust your image</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium mb-2">Original</h3>
                  <div className="bg-muted/30 rounded-md p-4 flex items-center justify-center">
                    <img 
                      src={resource.url_to_inject!} 
                      alt="Original"
                      className="max-h-[240px] object-contain"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-2">Preview</h3>
                  {/* <div className="bg-muted/30 rounded-md p-4 flex items-center justify-center">
                    <img 
                      src={editedImage || resource.url_to_inject!} 
                      alt="Preview"
                      className="max-h-[240px] object-contain"
                    />
                  </div> */}
                </div>
              </div>
              
              <div className="flex justify-center gap-4 py-2">
                <Button variant="outline" size="sm">
                  <Crop className="h-4 w-4 mr-2" />
                  Crop
                </Button>
                <Button variant="outline" size="sm">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Rotate
                </Button>
                <Button variant="outline" size="sm">
                  <Sliders className="h-4 w-4 mr-2" />
                  Adjust
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button>Save as New</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    );
  }
  
  // Regular card view
  return (
    <>
      <Card
        className={`overflow-hidden h-full flex flex-col transition-all duration-300 relative border-emerald-200/50 bg-white/90 backdrop-blur-sm ${
          pickerMode ? 'cursor-pointer hover:shadow-xl hover:shadow-emerald-100/50 hover:scale-[1.02]' : 'hover:shadow-xl hover:shadow-emerald-100/50 hover:scale-[1.02]'
        } ${
          selected ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-200/50 scale-[1.02] bg-emerald-50/40' : pickerMode ? 'hover:ring-2 hover:ring-emerald-300/50 hover:bg-emerald-50/20' : ''
        } ${focused ? 'ring-2 ring-emerald-400/70' : ''} ${
          isPendingUpload ? 'animate-pulse bg-emerald-50/30' : ''
        } ${hasUploadError ? 'border-red-300 bg-red-50/30' : ''}`}
        onMouseEnter={() => setShowHoverActions(true)}
        onMouseLeave={() => setShowHoverActions(false)}
        onClick={() => onToggleSelect && onToggleSelect(resource.id)}
        tabIndex={focusable ? 0 : undefined}
        onKeyDown={handleKeyboardNavigation}
        aria-selected={selected}
      >
      {/* Selection checkmark for picker mode */}
      {pickerMode && (
        <div className={`absolute top-3 right-3 z-10 rounded-full w-7 h-7 flex items-center justify-center transition-all shadow-md ${
          selected
            ? 'bg-emerald-500 text-white scale-110'
            : 'bg-white/90 text-muted-foreground border-2 border-emerald-200'
        } ${showHoverActions && !selected ? 'scale-105 border-emerald-300' : ''}`}>
          {selected ? <Check className="h-5 w-5 stroke-[3]" /> : <div className="h-5 w-5" />}
        </div>
      )}
    
      {shouldShowImagePreview ? (
        <div 
          className="relative w-full h-48 overflow-hidden cursor-pointer group"
          onClick={(e) => {
            if (!pickerMode) {
              e.stopPropagation();
              setShowFullImage(true);
            }
          }}
        >
          <img 
            src={resource.url_to_inject!} 
            alt={resource.description} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          
          {/* Subtle image overlay with info */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="text-white text-sm font-medium truncate">{resource.description}</div>
            <div className="text-white/80 text-xs">{displayName}</div>
          </div>
          
          {/* Hover action overlay - only in manager mode */}
          {!pickerMode && showHoverActions && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/90 hover:bg-white transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullImage(true);
                }}
              >
                <Expand className="h-4 w-4 mr-1" />
                View
              </Button>
            </div>
          )}
        </div>
      ) : shouldShowVideoPreview ? (
        <div 
          className="relative w-full h-48 overflow-hidden cursor-pointer group"
          onMouseEnter={() => setIsVideoHovered(true)}
          onMouseLeave={() => setIsVideoHovered(false)}
          onClick={(e) => {
            if (!pickerMode) {
              e.stopPropagation();
              setShowFullImage(true);
            }
          }}
        >
          <video 
            ref={videoRef}
            src={resource.url_to_inject!}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            muted
            loop
            playsInline
          />
          
          {/* Video overlay with play button when not hovered */}
          {!isVideoHovered && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="bg-white/90 rounded-full p-3 transition-all group-hover:scale-110">
                <Play className="h-6 w-6 text-gray-800" />
              </div>
            </div>
          )}
          
          {/* Subtle video overlay with info */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="text-white text-sm font-medium truncate">{resource.description}</div>
            <div className="text-white/80 text-xs">{displayName}</div>
          </div>
          
          {/* Hover action overlay - only in manager mode */}
          {!pickerMode && showHoverActions && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/90 hover:bg-white transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullImage(true);
                }}
              >
                <Expand className="h-4 w-4 mr-1" />
                View
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/90 hover:bg-white transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                disabled={!resource.url_to_inject}
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-red-600 hover:text-red-700 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(resource.id);
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 flex items-center justify-center bg-muted/30 h-48">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-muted mb-2">
              {resourceIcon}
            </div>
            <span className="text-sm font-medium">{resource.type}</span>
            <span className="text-xs text-muted-foreground mt-1">{displayName}</span>
          </div>
        </div>
      )}
      
      <div className="flex-grow p-4">
        <div className="flex items-center mb-2">
          {!pickerMode && onToggleSelect && (
            <Checkbox 
              checked={selected}
              onCheckedChange={() => onToggleSelect(resource.id)}
              aria-label="Select resource"
              className="mr-2"
            />
          )}
          <h3 className="font-medium text-base line-clamp-3 flex-grow">{resource.description}</h3>

        </div>
        
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {(!resource.url_to_inject || isPendingUpload || hasUploadError) && (
              <Badge variant={isPendingUpload ? "secondary" : "outline"} className={`px-2 py-0.5 font-normal ${
                isPendingUpload ? 'bg-blue-50 text-blue-700 border-blue-200' : ''
              } ${hasUploadError ? 'bg-red-50 text-red-700 border-red-200' : ''}`}>
                {isPendingUpload ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Uploading
                  </>
                ) : hasUploadError ? (
                  <>
                    <Trash2 className="h-3 w-3 mr-1" />
                    Upload Failed
                  </>
                ) : (
                  'Processing'
                )}
              </Badge>
            )}
            
            {isAIGenerated && (
              <Badge variant="secondary" className="px-2 py-0.5 font-normal">
                <Sparkles className="h-3 w-3 mr-1" /> AI-Generated
              </Badge>
            )}
          </div>
          
          {/* Metadata badges */}
          <div className="flex flex-wrap gap-1.5 mt-1">
            {resource.type && (
              <Badge variant="outline" className="text-xs px-2 py-0.5 h-5 bg-muted/30">
                {resource.type}
              </Badge>
            )}
            {formattedFileSize && (
              <Badge variant="outline" className="text-xs flex items-center gap-1 px-2 py-0.5 h-5 bg-muted/30">
                <FileIcon className="h-3 w-3" />
                {formattedFileSize}
              </Badge>
            )}
            {imageDimensions && (
              <Badge variant="outline" className="text-xs flex items-center gap-1 px-2 py-0.5 h-5 bg-muted/30">
                <ImageIcon className="h-3 w-3" />
                {imageDimensions.width}×{imageDimensions.height}
              </Badge>
            )}
            {videoDimensions && (
              <Badge variant="outline" className="text-xs flex items-center gap-1 px-2 py-0.5 h-5 bg-muted/30">
                <Video className="h-3 w-3" />
                {videoDimensions.width}×{videoDimensions.height}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-auto">
        <Separator />
        <div className="p-3">
          {!pickerMode && (shouldShowImagePreview || shouldShowVideoPreview) && (
            <Button
              variant="default"
              className="w-full mb-2"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullImage(true);
              }}
            >
              <Expand className="h-4 w-4 mr-2" />
              View
            </Button>
          )}
          <CardFooter className="p-0 flex justify-between bg-gradient-to-r from-emerald-50/50 to-teal-50/50 border-t border-emerald-200/30">
            {!pickerMode ? (
              <>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(resource.id);
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  disabled={!resource.url_to_inject}
                  className="hover:bg-primary/10"
                >
                  {resource.type === 'api' 
                    ? <ExternalLink className="h-4 w-4 mr-1" />
                    : <Download className="h-4 w-4 mr-1" />
                  }
                  {resource.type === 'api' ? 'Open' : 'Download'}
                </Button>
              </>
            ) : (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onToggleSelect) {
                    onToggleSelect(resource.id);
                  }
                }}
                className={selected ? "text-primary" : ""}
              >
                {selected ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Selected
                  </>
                ) : "Select"}
              </Button>
            )}
          </CardFooter>
        </div>
      </div>

      
      {/* Image Editing Modal - placeholder for now */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Image</DialogTitle>
            <DialogDescription>Crop, rotate, or adjust your image</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium mb-2">Original</h3>
                <div className="bg-muted/30 rounded-md p-4 flex items-center justify-center">
                  <img 
                    src={resource.url_to_inject!} 
                    alt="Original"
                    className="max-h-[240px] object-contain"
                  />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium mb-2">Preview</h3>
                {/* <div className="bg-muted/30 rounded-md p-4 flex items-center justify-center">
                  <img 
                    src={editedImage || resource.url_to_inject!} 
                    alt="Preview"
                    className="max-h-[240px] object-contain"
                  />
                </div> */}
              </div>
            </div>
            
            <div className="flex justify-center gap-4 py-2">
              <Button variant="outline" size="sm">
                <Crop className="h-4 w-4 mr-2" />
                Crop
              </Button>
              <Button variant="outline" size="sm">
                <RotateCcw className="h-4 w-4 mr-2" />
                Rotate
              </Button>
              <Button variant="outline" size="sm">
                <Sliders className="h-4 w-4 mr-2" />
                Adjust
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button>Save as New</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    
    {/* Single Media Preview Modal for both compact and regular views */}
    <Dialog open={showFullImage} onOpenChange={setShowFullImage}>
      <DialogContent className="sm:max-w-[1100px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="space-y-4">
            {sourceLabel && (
              <div>
                <Badge className="capitalize" variant="outline">Source: {String(sourceLabel).replace(/_/g, ' ')}</Badge>
              </div>
            )}
            <div>
              <Label htmlFor="resource-description" className="text-sm font-medium">Description</Label>
              <textarea
                id="resource-description"
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                className="mt-2 w-full min-h-[100px] p-3 border border-input bg-background rounded-md text-sm leading-relaxed resize-vertical focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Add a description for this resource..."
              />
            </div>
            {resource.url_to_inject && (
              <div>
                <a href={resource.url_to_inject} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                  <Link className="h-3 w-3" />
                  <span className="text-xs truncate">{resource.url_to_inject}</span>
                </a>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex justify-center mt-4">
          {shouldShowImagePreview ? (
            <img 
              src={resource.url_to_inject!} 
              alt={editedDescription || 'Resource image'} 
              className="max-w-full max-h-[65vh] object-contain bg-muted/30 rounded-md"
            />
          ) : shouldShowVideoPreview ? (
            <video 
              src={resource.url_to_inject!}
              controls
              className="max-w-full max-h-[65vh] object-contain bg-muted/30 rounded-md"
              autoPlay
              muted
            >
              Your browser does not support the video tag.
            </video>
          ) : null}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex flex-wrap gap-2 justify-start sm:mr-auto">
            {imageDimensions && (
              <Badge className="flex items-center gap-1">
                <ImageIcon className="h-3 w-3" />
                {imageDimensions.width}×{imageDimensions.height}
              </Badge>
            )}
            {videoDimensions && (
              <Badge className="flex items-center gap-1">
                <Video className="h-3 w-3" />
                {videoDimensions.width}×{videoDimensions.height}
              </Badge>
            )}
            {formattedFileSize && (
              <Badge className="flex items-center gap-1" variant="outline">
                <FileIcon className="h-3 w-3" />
                {formattedFileSize}
              </Badge>
            )}
            <Badge className="flex items-center gap-1" variant="outline">
              <Calendar className="h-3 w-3" />
              {date}
            </Badge>
            {isAIGenerated && (
              <Badge className="flex items-center gap-1" variant="secondary">
                <Sparkles className="h-3 w-3" />
                AI-Generated
              </Badge>
            )}
          </div>
          <div className="flex gap-2 mt-2 sm:mt-0">
            <Button variant="outline" onClick={handleDownload}>Download</Button>
            <Button onClick={async () => {
              // Update the resource with new description
              if (editedDescription !== (resource.description || '')) {
                try {
                  const { error } = await supabase
                    .from('resource_cache')
                    .update({
                      description: editedDescription
                    })
                    .eq('id', resource.id);
                  
                  if (error) throw error;
                  
                  // Update local state
                  resource.description = editedDescription;
                  
                  toast({
                    title: 'Resource Updated',
                    description: 'The resource description has been updated.',
                    variant: 'default',
                    className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
                  });
                } catch (error) {
                  console.error('Error updating resource:', error);
                  toast({
                    title: 'Update Failed',
                    description: 'Failed to update the resource.',
                    variant: 'destructive',
                  });
                }
              }
              setShowFullImage(false);
            }}>Save & Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

// Add this polling function before the BrandResources component
const pollResourceStatus = async (
  resourceId: string,
  onUpdate: (resource: ResourceCache) => void,
  maxAttempts = 30,
  interval = 2000
): Promise<void> => {
  let attempts = 0;

  const checkStatus = async () => {
    const { data, error } = await supabase
      .from('resource_cache')
      .select('*')
      .eq('id', resourceId)
      .single();

    if (error) {
      console.error('Error polling resource status:', error);
      return true; // Stop polling on error
    }

    if (data && data.processed) {
      onUpdate(data);
      return true; // Stop polling when processed
    }

    attempts++;
    return attempts >= maxAttempts; // Stop if max attempts reached
  };

  return new Promise((resolve) => {
    const poll = async () => {
      const shouldStop = await checkStatus();
      
      if (shouldStop) {
        resolve();
      } else {
        setTimeout(poll, interval);
      }
    };

    poll();
  });
};

// New AI Generation Modal Component
const GenerateWithAIModal: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImageGenerated: (resource: ResourceCache) => void;
  brandId: string;
}> = ({ open, onOpenChange, onImageGenerated, brandId }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<ResourceCache | null>(null);
  const [stylePreset, setStylePreset] = useState('realistic');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  
  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      // Keep history but reset other fields
      setPrompt('');
      setGeneratedImage(null);
      setLoading(false);
    }
  }, [open]);
  
  const stylePresets = [
    { value: 'realistic', label: 'Realistic' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'product', label: 'Product' },
    { value: 'abstract', label: 'Abstract' },
    { value: 'cartoon', label: 'Cartoon' },
    { value: 'artistic', label: 'Artistic' },
  ];
  
  const aspectRatios = [
    { value: '1:1', label: 'Square (1:1)', width: 1024, height: 1024 },
    { value: '16:9', label: 'Landscape (16:9)', width: 1344, height: 768 },
    { value: '9:16', label: 'Portrait (9:16)', width: 768, height: 1344 },
    { value: '4:5', label: 'Instagram (4:5)', width: 820, height: 1024 },
    { value: '2:1', label: 'Banner (2:1)', width: 1344, height: 672 },
  ];
  
  const handleGenerateImage = async () => {
    if (!brandId || !prompt.trim()) return;
    
    try {
      setLoading(true);
      
      // Add prompt to history
      if (!promptHistory.includes(prompt)) {
        setPromptHistory(prev => [prompt, ...prev.slice(0, 9)]);
      }
      
      // Get dimensions from selected aspect ratio
      const selectedAspect = aspectRatios.find(a => a.value === aspectRatio);
      const width = selectedAspect?.width || 1024;
      const height = selectedAspect?.height || 1024;
      
      // Create placeholder resource first
      const placeholder = `image_ai_${Date.now()}`;
      const description = prompt.substring(0, 100) + (prompt.length > 100 ? '...' : '');
      
      const { data: resource, error } = await supabase
        .from('resource_cache')
        .insert({
          brand_id: brandId,
          description: description,
          placeholder,
          type: 'image',
          processed: false,
          required: false,
          metadata: {
            source: 'ai_generated',
            ai_generated: true,
            ai_prompt: prompt,
            ai_style: stylePreset,
            width,
            height,
            aspect_ratio: aspectRatio,
          }
        })
        .select()
        .single();
      
      if (error) throw new Error(error.message);
      
      // Call worker to generate image
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      if (!workerUrl) {
        throw new Error('Worker URL not configured');
      }
      
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No active session');
      }
      
      const response = await fetch(`${workerUrl}/rainfactory/content-functions/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({
          resourceId: resource.id,
          prompt,
          style: stylePreset,
          width,
          height,
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate image');
      }
      
      // Poll for resource updates
      await pollResourceStatus(
        resource.id,
        (updatedResource) => {
          setGeneratedImage(updatedResource);
          onImageGenerated(updatedResource);
        }
      );
      
      toast({
        title: 'Image Generated',
        description: 'Your AI-generated image is ready',
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });
      
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'An error occurred while generating the image',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const getImageDimension = () => {
    const selectedAspect = aspectRatios.find(a => a.value === aspectRatio);
    return `${selectedAspect?.width || 1024}×${selectedAspect?.height || 1024}`;
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Generate Image with AI</DialogTitle>
          <DialogDescription>
            Create a new image using AI by providing a detailed prompt
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="relative">
            <div className="flex">
              <div className="relative flex-1">
                <Input
                  placeholder="Describe the image you want to create..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="pr-9"
                />
                {promptHistory.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                    onClick={() => setShowPromptHistory(!showPromptHistory)}
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Button 
                className="ml-2"
                onClick={handleGenerateImage}
                disabled={loading || !prompt.trim()}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate
                  </>
                )}
              </Button>
            </div>
            
            {/* Prompt history dropdown */}
            {showPromptHistory && promptHistory.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-background rounded-md border shadow-lg">
                <div className="p-2">
                  <h4 className="text-sm font-medium mb-1">Recent Prompts</h4>
                  <div className="max-h-32 overflow-y-auto">
                    {promptHistory.map((historyPrompt, index) => (
                      <div 
                        key={index}
                        className="px-2 py-1.5 text-sm rounded hover:bg-muted cursor-pointer"
                        onClick={() => {
                          setPrompt(historyPrompt);
                          setShowPromptHistory(false);
                        }}
                      >
                        {historyPrompt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="style-preset">Style Preset</Label>
              <Select
                value={stylePreset}
                onValueChange={setStylePreset}
              >
                <SelectTrigger id="style-preset">
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent>
                  {stylePresets.map(preset => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="aspect-ratio">Aspect Ratio</Label>
              <Select
                value={aspectRatio}
                onValueChange={setAspectRatio}
              >
                <SelectTrigger id="aspect-ratio">
                  <SelectValue placeholder="Select ratio" />
                </SelectTrigger>
                <SelectContent>
                  {aspectRatios.map(ratio => (
                    <SelectItem key={ratio.value} value={ratio.value}>
                      {ratio.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {getImageDimension()} pixels
              </p>
            </div>
          </div>
          
          {/* Preview area */}
          {generatedImage && generatedImage.url_to_inject && (
            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">Generated Image</h4>
              <div className="flex justify-center bg-muted/30 rounded-md p-4">
                <img 
                  src={generatedImage.url_to_inject} 
                  alt={generatedImage.description} 
                  className="max-h-[300px] rounded-md object-contain"
                />
              </div>
              <div className="flex justify-center mt-2">
                <Badge className="flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" />
                  {getImageDimension()}
                </Badge>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="sm:ml-auto"
          >
            {generatedImage ? 'Close' : 'Cancel'}
          </Button>
          {generatedImage && (
            <Button
              onClick={() => {
                onImageGenerated(generatedImage);
                onOpenChange(false);
              }}
            >
              <Check className="mr-2 h-4 w-4" />
              Use Image
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const addNewOptions = [
  {
    label: 'Upload Files',
    description: 'Upload images, videos, audio, or documents to your brand resources.',
    icon: <Upload className="h-4 w-4 mr-2" />,
    action: 'upload',
  },
  {
    label: 'Search Stock',
    description: 'Find high-quality free photos and videos from stock libraries.',
    icon: <Search className="h-4 w-4 mr-2" />,
    action: 'pexels',
  },
  {
    label: 'Generate with AI',
    description: 'Create a new image using AI by providing a prompt.',
    icon: <Sparkles className="h-4 w-4 mr-2" />,
    action: 'ai',
  },
];

const AddNewButton: React.FC<{
  onUploadClick: () => void;
  onGenerateAIClick: () => void;
  onPexelsClick: () => void;
  isCompact?: boolean;
}> = ({ onUploadClick, onGenerateAIClick, onPexelsClick }) => {
  const [selectedIndex, setSelectedIndex] = useState('0');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleMainClick = () => {
    // Just open the dropdown
    setDropdownOpen(true);
  };
  
  const handleDropdownItemClick = (value: string) => {
    setSelectedIndex(value);
    // Immediately trigger the action
    const action = addNewOptions[Number(value)].action;
    if (action === 'upload') {
      onUploadClick();
    } else if (action === 'pexels') {
      onPexelsClick();
    } else if (action === 'ai') {
      onGenerateAIClick();
    } else {
      setShowComingSoon(true);
    }
    // Close dropdown after selection
    setDropdownOpen(false);
  };

  return (
    <>
      <div className="inline-flex -space-x-px divide-x divide-primary-foreground/30 rounded-lg shadow-sm shadow-black/5 rtl:space-x-reverse">
        <Button
          className="rounded-none shadow-none first:rounded-s-lg last:rounded-e-lg focus-visible:z-10"
          onClick={handleMainClick}
        >
          {addNewOptions[Number(selectedIndex)].icon}
          Add Files
        </Button>
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className="rounded-none shadow-none first:rounded-s-lg last:rounded-e-lg focus-visible:z-10"
              size="icon"
              aria-label="Options"
            >
              <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="max-w-64 md:max-w-xs"
            side="bottom"
            sideOffset={4}
            align="end"
          >
            <DropdownMenuRadioGroup value={selectedIndex} onValueChange={handleDropdownItemClick}>
              {addNewOptions.map((option, index) => (
                <DropdownMenuRadioItem
                  key={option.label}
                  value={String(index)}
                  className="items-start [&>span]:pt-1.5 cursor-pointer"
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium flex items-center">{option.icon}{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </div>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Dialog open={showComingSoon} onOpenChange={setShowComingSoon}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Coming Soon</DialogTitle>
            <DialogDescription>
              The "Generate with AI" feature is coming soon. Stay tuned!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowComingSoon(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Preview Tray Component for Picker Mode
const PreviewTray: React.FC<{
  selectedResource: ResourceCache | null;
  onRemove: () => void;
}> = ({ selectedResource, onRemove }) => {
  if (!selectedResource) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-3 flex items-center justify-between z-10">
      <div className="flex items-center">
        <div className="h-12 w-12 rounded overflow-hidden mr-3 border">
          {selectedResource.type === 'image' && selectedResource.url_to_inject ? (
            <img 
              src={selectedResource.url_to_inject} 
              alt={selectedResource.description} 
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-muted">
              {getResourceIcon(selectedResource.type, selectedResource.http_content_type)}
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-medium">{selectedResource.description}</p>
          <p className="text-xs text-muted-foreground">Selected</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </div>
  );
};

// Upload Modal Component for Picker Mode
const UploadModal: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilesUploaded: (files: File[]) => void;
  onAddResource: (
    type: ResourceType, 
    description: string,
    placeholder: string,
    httpContentType?: string | null,
    urlToInject?: string | null
  ) => Promise<void>;
}> = ({ open, onOpenChange, onFilesUploaded, onAddResource }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Resources</DialogTitle>
          <DialogDescription>
            Upload files to your brand resources
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <DragAndDropUploadZone 
            onFilesUploaded={(files) => {
              onFilesUploaded(files);
              onOpenChange(false);
            }}
            onAddResource={async (...args) => {
              await onAddResource(...args);
              onOpenChange(false);
            }}
            isCompact={false}
          />
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BrandResources: React.FC<BrandResourcesProps> = ({ 
  mode = 'manager', 
  onResourceSelect,
  initialSelectedResource,
  onCancel,
  allowedTypes,
  brandId: propBrandId // Accept the brandId prop
}) => {
  const { id: urlBrandId } = useParams<{ id: string }>();
  // Use the prop brandId if provided, otherwise use the URL parameter
  const brandId = propBrandId || urlBrandId || '';
  
  const [resources, setResources] = useState<ResourceCache[]>([]);
  const [filteredResources, setFilteredResources] = useState<ResourceCache[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [resourceType, setResourceType] = useState<'all' | ResourceType>('all');
  // Source filtering: default to all sources visible
  const SOURCE_OPTIONS = [
    'news_scraping',
    'website_scraping',
    // 'convergence_analysis',
    'ai_generated',
    'stock_media',
    'user_upload',
    'external_url'
  ] as const;
  type SourceOption = typeof SOURCE_OPTIONS[number];
  const [selectedSources, setSelectedSources] = useState<Set<SourceOption>>(new Set<SourceOption>(SOURCE_OPTIONS as unknown as SourceOption[]));
  const toggleSource = (source: SourceOption) => {
    setSelectedSources(prev => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source); else next.add(source);
      return next;
    });
  };
  const areAllSourcesSelected = selectedSources.size === SOURCE_OPTIONS.length;
  const toggleAllSources = () => {
    setSelectedSources(prev => prev.size === SOURCE_OPTIONS.length
      ? new Set<SourceOption>()
      : new Set<SourceOption>(SOURCE_OPTIONS as unknown as SourceOption[])
    );
  };
  const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set());
  const [selectedResource, setSelectedResource] = useState<ResourceCache | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [generateAIModalOpen, setGenerateAIModalOpen] = useState(false);
  const [pexelsSearchOpen, setPexelsSearchOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest');
  const [focusedResourceId, setFocusedResourceId] = useState<string | null>(null);
  
  // Load resources on mount
  useEffect(() => {
    const fetchResources = async () => {
      if (!brandId) return;
      
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('resource_cache')
          .select('*')
          .eq('brand_id', brandId)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        setResources(data || []);
        setFilteredResources(data || []);
      } catch (error) {
        console.error('Error fetching resources:', error);
        toast({
          title: 'Failed to load resources',
          description: error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchResources();
  }, [brandId]);
  
  // Initial selection if in picker mode
  useEffect(() => {
    if (mode === 'picker' && initialSelectedResource && resources.length > 0) {
      const resource = resources.find(r => r.id === initialSelectedResource);
      if (resource) {
        setSelectedResources(new Set([resource.id]));
        setSelectedResource(resource);
      }
    }
  }, [mode, initialSelectedResource, resources]);
  
  // Apply filters
  useEffect(() => {
    let filtered = [...resources];
    
    // Filter by selected sources
    filtered = filtered.filter(resource => {
      const md = resource.metadata;
      if (!isResourceMetadata(md)) return false;
      const src = md.source as SourceOption;
      return selectedSources.has(src);
    });
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        resource => 
          resource.description?.toLowerCase().includes(query) || 
          resource.placeholder?.toLowerCase().includes(query) ||
          resource.url_to_inject?.toLowerCase().includes(query)
      );
    }
    
    // Filter by resource type
    if (resourceType !== 'all') {
      filtered = filtered.filter(resource => resource.type === resourceType);
    }
    
    // Filter by allowed types (for picker mode)
    if (allowedTypes && allowedTypes.length > 0) {
      filtered = filtered.filter(resource => 
        allowedTypes.includes(resource.type)
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'name':
          return (a.description || '').localeCompare(b.description || '');
        default:
          return 0;
      }
    });
    
    setFilteredResources(filtered);
  }, [resources, searchQuery, resourceType, sortBy, allowedTypes, selectedSources]);
  
  // Update selected resource when selectedResources changes (for picker mode)
  useEffect(() => {
    if (mode === 'picker') {
      if (selectedResources.size === 0) {
        setSelectedResource(null);
      } else {
        const selectedId = Array.from(selectedResources)[0];
        const resource = resources.find(r => r.id === selectedId);
        setSelectedResource(resource || null);
      }
    }
  }, [selectedResources, resources, mode]);
  
  // Toggle selection of a resource
  const handleToggleSelect = useCallback((id: string) => {
    if (mode === 'picker') {
      // In picker mode, we can only select one resource
      setSelectedResources(new Set([id]));
      const resource = resources.find(r => r.id === id);
      if (resource && onResourceSelect) {
        onResourceSelect(resource);
      }
    } else {
      // In manager mode, we can select multiple resources
      setSelectedResources(prev => {
        const newSelection = new Set(prev);
        if (newSelection.has(id)) {
          newSelection.delete(id);
        } else {
          newSelection.add(id);
        }
        return newSelection;
      });
    }
  }, [mode, resources, onResourceSelect]);
  
  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('resource_cache')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      setResources(prev => prev.filter(resource => resource.id !== id));
      setSelectedResources(prev => {
        const newSelection = new Set(prev);
        newSelection.delete(id);
        return newSelection;
      });
      
      toast({
        title: 'Resource Deleted',
        description: 'The resource has been deleted successfully',
        variant: 'default',
      });
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast({
        title: 'Failed to Delete Resource',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };
  
  const handleBulkDelete = async () => {
    try {
      const selectedIds = Array.from(selectedResources);
      
      for (const id of selectedIds) {
        const { error } = await supabase
          .from('resource_cache')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
      }
      
      setResources(prev => prev.filter(resource => !selectedResources.has(resource.id)));
      setSelectedResources(new Set());
      
      toast({
        title: 'Resources Deleted',
        description: `${selectedIds.length} resources have been deleted successfully`,
        variant: 'default',
      });
    } catch (error) {
      console.error('Error deleting resources:', error);
      toast({
        title: 'Failed to Delete Resources',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };
  
  const handleFilesUploaded = async (files: File[]) => {
    if (!brandId || files.length === 0) return;
    
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No active session');
      }
      
      // Process files one by one
      const pendingResources: ResourceCache[] = [];
      
      // First, create pending resources for immediate display
      for (const file of files) {
        // Determine the resource type based on file type
        let resourceType: ResourceType = 'document';
        
        if (file.type.startsWith('image/')) {
          resourceType = 'image';
        } else if (file.type.startsWith('video/')) {
          resourceType = 'video';
        } else if (file.type.startsWith('audio/')) {
          resourceType = 'audio';
        } else if (
          file.type.startsWith('application/json') ||
          file.type.startsWith('text/csv') ||
          file.type.endsWith('spreadsheetml.sheet')
        ) {
          resourceType = 'data';
        }
        
        // Create a placeholder in memory first for immediate display,
        // we'll create the actual resource in the database later
        const placeholder = `${resourceType}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9]/g, '_')}`;
        
        // Create a new resource for upload now and display right away
        const { data: resource, error: createError } = await supabase
          .from('resource_cache')
          .insert({
            brand_id: brandId,
            description: file.name,
            placeholder,
            type: resourceType,
            processed: false,
            required: false,
            http_content_type: file.type,
            metadata: {
              source: 'user_upload',
              file_size: file.size,
              file_name: file.name,
              mime_type: file.type,
              pending_upload: true
            }
          })
          .select()
          .single();
        
        if (createError) throw createError;
        
        // Add to our pending resources array for tracking
        pendingResources.push(resource);
        
        // Add to state so it's displayed immediately at the top
        setResources(prev => [resource, ...prev]);
      }

      // Auto-focus the first pending resource
      if (pendingResources.length > 0) {
        setFocusedResourceId(pendingResources[0].id);
        
        // Auto-select if in picker mode
        if (mode === 'picker') {
          setSelectedResources(new Set([pendingResources[0].id]));
        }
      }
      
      // Now, process the actual uploads in the background
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const pendingResource = pendingResources[i];
        
        try {
          // Upload the file to storage
          const formData = new FormData();
          formData.append('file', file);
          formData.append('resourceId', pendingResource.id);
          formData.append('brandId', brandId);
          
          const workerUrl = import.meta.env.VITE_WORKERS_URL;
          if (!workerUrl) {
            throw new Error('Worker URL not configured');
          }
          
          // Start the actual upload
          const response = await fetch(`${workerUrl}/rainfactory/content-functions/resource-upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.data.session.access_token}`
            },
            body: formData
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to upload resource');
          }
          
          // Poll for resource updates
          pollResourceStatus(
            pendingResource.id,
            (updatedResource) => {
              setResources(prev => prev.map(r => 
                r.id === updatedResource.id ? updatedResource : r
              ));
            }
          );
        } catch (uploadError) {
          console.error(`Error uploading file ${file.name}:`, uploadError);
          // Update the resource to show the error
          setResources(prev => prev.map(r => 
            r.id === pendingResource.id ? {
              ...r,
              metadata: {
                ...r.metadata as object,
                upload_error: true,
                error_message: uploadError instanceof Error ? uploadError.message : 'Upload failed'
              }
            } : r
          ));
        }
      }
      
      toast({
        title: 'Files Uploading',
        description: `${files.length} ${files.length === 1 ? 'file' : 'files'} being processed...`,
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });
      
    } catch (error) {
      console.error('Error preparing uploads:', error);
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'An error occurred preparing uploads',
        variant: 'destructive',
      });
    }
  };
  
  const handleAddResource = async (
    type: ResourceType, 
    description: string,
    placeholder: string,
    httpContentType?: string | null,
    urlToInject?: string | null
  ) => {
    if (!brandId) return;
    
    try {
      const { data: resource, error } = await supabase
        .from('resource_cache')
        .insert({
          brand_id: brandId,
          description,
          placeholder,
          type,
          processed: true, // External resources are immediately available
          required: false,
          http_content_type: httpContentType,
          url_to_inject: urlToInject,
          metadata: {
            source: 'external_url',
          }
        })
        .select()
        .single();
      
      if (error) throw error;
      
      setResources(prev => [resource, ...prev]);
      
      toast({
        title: 'Resource Added',
        description: 'External resource has been added successfully',
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });
      
    } catch (error) {
      console.error('Error adding resource:', error);
      toast({
        title: 'Failed to Add Resource',
        description: error instanceof Error ? error.message : 'An error occurred adding the resource',
        variant: 'destructive',
      });
    }
  };
  
  // Handle keyboard navigation for picker mode
  const handleResourceKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, id: string) => {
    if (mode !== 'picker') return;
    
    switch (e.key) {
      case 'Enter':
        // Select the focused resource
        handleToggleSelect(id);
        break;
      case 'ArrowRight':
      case 'ArrowLeft':
      case 'ArrowUp':
      case 'ArrowDown':
        e.preventDefault();
        // Find current index
        const currentIndex = filteredResources.findIndex(r => r.id === id);
        if (currentIndex === -1) return;
        
        let newIndex = currentIndex;
        const columns = Math.floor(window.innerWidth / 300) || 3; // Estimate columns based on card width
        
        switch (e.key) {
          case 'ArrowRight':
            newIndex = Math.min(currentIndex + 1, filteredResources.length - 1);
            break;
          case 'ArrowLeft':
            newIndex = Math.max(currentIndex - 1, 0);
            break;
          case 'ArrowUp':
            newIndex = Math.max(currentIndex - columns, 0);
            break;
          case 'ArrowDown':
            newIndex = Math.min(currentIndex + columns, filteredResources.length - 1);
            break;
        }
        
        if (newIndex !== currentIndex) {
          setFocusedResourceId(filteredResources[newIndex].id);
        }
        break;
    }
  };
  
  // Render empty state
  const renderEmptyState = () => {
    if (isLoading) return null;

    return (
      <div className="relative overflow-hidden bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/40 rounded-2xl border border-emerald-200/50 shadow-sm p-12">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />

        <div className="relative flex flex-col items-center justify-center text-center">
          <div className="p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg mb-6">
            <DatabaseIcon className="h-12 w-12 text-emerald-500" />
          </div>

          <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent mb-3">
            {searchQuery || resourceType !== 'all' ? 'No resources found' : 'Build Your Resource Library'}
          </h3>

          <p className="text-emerald-600 mb-8 max-w-lg text-lg leading-relaxed">
            {searchQuery || resourceType !== 'all'
              ? 'Try adjusting your search or filters to find what you\'re looking for, or upload new resources to expand your library.'
              : 'Start by uploading your brand assets, generating AI content, or adding external resources to create a comprehensive media library.'}
          </p>

          <div className="flex flex-col gap-6 items-center">
            <AddNewButton
              onUploadClick={() => setUploadModalOpen(true)}
              onGenerateAIClick={() => setGenerateAIModalOpen(true)}
              onPexelsClick={() => setPexelsSearchOpen(true)}
            />

            <div className="max-w-lg bg-white/60 backdrop-blur-sm rounded-xl border border-emerald-200/50 shadow-sm p-6">
              <h4 className="font-semibold text-emerald-800 mb-3 flex items-center justify-center">
                <HelpCircle className="h-5 w-5 mr-2 text-emerald-600" />
                Resource Types You Can Add
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center text-emerald-700">
                  <ImageIcon className="h-4 w-4 mr-2 text-emerald-500" />
                  Images & Photos
                </div>
                <div className="flex items-center text-emerald-700">
                  <Video className="h-4 w-4 mr-2 text-emerald-500" />
                  Videos & Media
                </div>
                <div className="flex items-center text-emerald-700">
                  <FileText className="h-4 w-4 mr-2 text-emerald-500" />
                  Documents
                </div>
                <div className="flex items-center text-emerald-700">
                  <Sparkles className="h-4 w-4 mr-2 text-emerald-500" />
                  AI Generated
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className={`container mx-auto ${mode === 'picker' ? 'h-[calc(100vh-140px)] flex flex-col' : 'mt-8'}`}>
      {/* Enhanced Header - only show header in manager mode */}
      {mode === 'manager' && (
        <div className="mb-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/40 rounded-2xl border border-emerald-200/50 shadow-sm p-8">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="relative">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                  <DatabaseIcon className="h-8 w-8 text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-700 via-teal-700 to-cyan-700 bg-clip-text text-transparent mb-2">
                    Brand Resources
                  </h1>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Manage your brand's visual assets and resources library. Upload, organize, and access all media files for content creation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Controls Section */}
      <div className="bg-white/60 backdrop-blur-sm rounded-xl border border-emerald-200/50 shadow-sm p-4 mb-6">
        <div className="flex justify-between items-center">
        <div className="flex items-center flex-1">
          {/* Search input */}
          <div className="relative w-full md:w-64 mr-4">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search resources..."
              className="pl-9 w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Type filter tabs - only show in manager mode */}
          {mode === 'manager' && (
            <Tabs
              value={resourceType}
              onValueChange={(value) => setResourceType(value as ResourceType | 'all')}
              className="hidden md:flex"
            >
              <TabsList className="bg-white/80 backdrop-blur-sm shadow-sm border border-emerald-200/50">
                <TabsTrigger value="all" className="data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700">
                  <Folder className="h-4 w-4 mr-1" /> All
                </TabsTrigger>
                <TabsTrigger value="image" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
                  <ImageIcon className="h-4 w-4 mr-1 text-blue-500" /> Images
                </TabsTrigger>
                <TabsTrigger value="video" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
                  <Video className="h-4 w-4 mr-1 text-red-500" /> Videos
                </TabsTrigger>
                <TabsTrigger value="audio" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                  <AudioLines className="h-4 w-4 mr-1 text-purple-500" /> Audio
                </TabsTrigger>
                <TabsTrigger value="document" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700">
                  <FileText className="h-4 w-4 mr-1 text-orange-500" /> Documents
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 bg-white/80 backdrop-blur-sm border-emerald-200/50 hover:bg-emerald-50">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSortBy('newest')}>
                Newest first
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('oldest')}>
                Oldest first
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('name')}>
                Name (A-Z)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Sources filter dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 bg-white/80 backdrop-blur-sm border-emerald-200/50 hover:bg-emerald-50">
                <Sliders className="h-4 w-4 mr-2" />
                Sources
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={toggleAllSources}>
                <div className="flex items-center gap-2">
                  <Checkbox checked={areAllSourcesSelected} />
                  <span className="font-medium">All</span>
                </div>
              </DropdownMenuItem>
              <Separator className="my-1" />
              {SOURCE_OPTIONS.map((src) => (
                <DropdownMenuItem key={src} onClick={() => toggleSource(src as any)}>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={selectedSources.has(src as any)} />
                    <span className="capitalize">{String(src).replace(/_/g, ' ')}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Add New button */}
          <AddNewButton 
            onUploadClick={() => setUploadModalOpen(true)}
            onGenerateAIClick={() => setGenerateAIModalOpen(true)}
            onPexelsClick={() => setPexelsSearchOpen(true)}
            isCompact={mode === 'picker'}
          />
          
          {/* Bulk delete button in manager mode */}
          {mode === 'manager' && selectedResources.size > 0 && (
            <Button 
              variant="destructive"
              onClick={() => setBulkDeleteConfirmOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedResources.size})
            </Button>
          )}
          
          {/* Confirm button in picker mode when something is selected */}
          {mode === 'picker' && selectedResource && onResourceSelect && (
            <Button onClick={() => onResourceSelect(selectedResource)}>
              <Check className="h-4 w-4 mr-2" />
              Choose
            </Button>
          )}
          
          {/* Cancel button for picker mode */}
          {mode === 'picker' && onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
        </div>
      </div>
      
      {/* Resource grid with proper scrolling in picker mode */}
      <div className={mode === 'picker' ? 'flex-1 overflow-y-auto pr-2' : ''}>
        {/* Enhanced Loading state */}
        {isLoading ? (
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/40 rounded-2xl border border-emerald-200/50 shadow-sm p-12">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="flex flex-col justify-center items-center text-center">
              <div className="p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg mb-4">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-500" />
              </div>
              <h3 className="text-xl font-semibold text-emerald-800 mb-2">Loading resources...</h3>
              <p className="text-emerald-600">Fetching your brand assets and media files</p>
            </div>
          </div>
        ) : filteredResources.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${mode === 'picker' ? 'lg:grid-cols-3' : 'lg:grid-cols-3 xl:grid-cols-4'} gap-6 mb-16`}>
            {filteredResources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onDelete={() => {
                  setSelectedResource(resource);
                  setDeleteConfirmOpen(true);
                }}
                selected={selectedResources.has(resource.id)}
                onToggleSelect={handleToggleSelect}
                pickerMode={mode === 'picker'}
                focusable={mode === 'picker'}
                focused={focusedResourceId === resource.id}
                onKeyDown={handleResourceKeyDown}
                compactMode={mode === 'picker'}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Modal dialogs */}
      {/* Single Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-gradient-to-br from-white via-red-50/30 to-red-50/40 border border-red-200/50">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <DialogTitle className="text-xl font-bold text-red-800">Are you sure?</DialogTitle>
            <DialogDescription className="text-red-600">
              This action cannot be undone. This will permanently delete the selected resource.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (selectedResource) {
                  handleDelete(selectedResource.id);
                  setSelectedResources(new Set());
                  setSelectedResource(null);
                  setDeleteConfirmOpen(false);
                }
              }}
              variant="destructive"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <DialogContent className="bg-gradient-to-br from-white via-red-50/30 to-red-50/40 border border-red-200/50">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <DialogTitle className="text-xl font-bold text-red-800">Delete {selectedResources.size} resources?</DialogTitle>
            <DialogDescription className="text-red-600">
              This action cannot be undone. This will permanently delete all selected resources.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setBulkDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                handleBulkDelete();
                setBulkDeleteConfirmOpen(false);
              }}
              variant="destructive"
            >
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Upload Modal for Picker Mode */}
      <UploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onFilesUploaded={handleFilesUploaded}
        onAddResource={handleAddResource}
      />
      
      {/* Generate with AI Modal */}
      <GenerateWithAIModal
        open={generateAIModalOpen}
        onOpenChange={setGenerateAIModalOpen}
        onImageGenerated={(resource) => {
          // Auto-select generated image and close modal
          if (mode === 'picker') {
            setSelectedResources(new Set([resource.id]));
            if (onResourceSelect) {
              onResourceSelect(resource);
            }
          }
        }}
        brandId={brandId || ''}
      />
      
      {/* Pexels Search Modal */}
      <PexelsSearchModal
        open={pexelsSearchOpen}
        onOpenChange={setPexelsSearchOpen}
        onMediaSelected={() => {
          // Refresh resources list
          const fetchResources = async () => {
            if (!brandId) return;
            
            try {
              const { data, error } = await supabase
                .from('resource_cache')
                .select('*')
                .eq('brand_id', brandId)
                .order('created_at', { ascending: false });
              
              if (error) throw error;
              
              setResources(data || []);
            } catch (error) {
              console.error('Error refreshing resources:', error);
            }
          };
          
          fetchResources();
        }}
        brandId={brandId || ''}
      />
      
      {/* Preview Tray for Picker Mode */}
      {mode === 'picker' && selectedResource && (
        <PreviewTray 
          selectedResource={selectedResource} 
          onRemove={() => {
            setSelectedResources(new Set());
            setSelectedResource(null);
          }}
        />
      )}
    </div>
  );
};

export default BrandResources;